import { instance } from '../api/api';
import { hasApiErrorCode, toApiError } from '../utils/apiError';

// Интерфейсы для работы с API автомобилей
export interface IAutopark {
  id: string;
  tradeName: string;
  legalName: string;
  actualAddress: string;
  balance: number;
  bankName: string;
  bik: string;
  currentAccount: string;
  email: string;
  externalID: string;
  fullnameEmployee: string;
  fullnameOwner: string;
  inn: string;
  kpp: string;
  legalAddress: string;
  ogrn: string;
  phone: string;
}

export interface ICarImage {
  id: string;
  carID: string;
  photoUrl: string;
  photoType: string;
  createdAt: string;
}

export interface ICar {
  id: string;
  number: string;
  mark: string;
  model: string;
  autopark: IAutopark;
  autoparkID: string;
  carType: string;
  createdAt: string;
  diagnosticCardNumber: number;
  diagnosticCardPhoto: string;
  diagnosticCardVerified: boolean;
  driverID: string;
  issueDate: string;
  osagoNumber: string;
  osagoPhoto: string;
  taxiLicense: string;
  updatedAt: string;
  validityPeriodOfDC: string;
  validityPeriodOfOsago: string;
  autonegotiation: boolean;
  autonegotiationTechnikID: string | null;
  carImages: ICarImage[];
}

export interface ICarsResponse {
  data: ICar[];
  page: number;
  totalCount: number;
  totalPages: number;
}

export interface ICarModel {
  id: number;
  mark: string;
  model: string;
  startYear: number;
  endYear: number;
}

// Интерфейс для обновления автомобиля (только поля из API PUT /cars/{id})
export interface ICarUpdateRequest {
  number: string;
  mark: string;
  model: string;
  taxiLicense: string;
  issueDate: string;
  validityPeriodOfDC: string;
  diagnosticCardNumber: number;
}

// Интерфейс для обновления данных ОСАГО (API PUT /cars/{id}/osago-details)
export interface IOsagoUpdateRequest {
  osagoNumber: string;
  validityPeriodOfOsago: string;
}

// Интерфейс для обновления фотографий кузова автомобиля (API PUT /cars/{id}/photos)
export interface ICarPhotosUpdateRequest {
  front?: File;
  back?: File;
  left?: File;
  right?: File;
  trunk?: File;
}

// Интерфейс для создания нового автомобиля (API POST /cars/admin/create)
export interface ICreateCarRequest {
  carType: string;
  mark: string;
  model: string;
  number: string;
  autoparkID: string;
}

export const CAR_FORBIDDEN_MESSAGE =
  'Автосогласование назначено другим техником, снять его может только он или администратор';

// 403 car_forbidden: техник снимает чужое автосогласование
export function isCarForbidden(error: unknown): boolean {
  return hasApiErrorCode(error, 403, 'car_forbidden');
}

// Бэкенд читает technikID только из query; без параметра техник автосогласования снимается
function carTechnikUrl(carId: string, technikID: string | null): string {
  const url = `/cars/${carId}/technik`;
  return technikID === null ? url : `${url}?technikID=${encodeURIComponent(technikID)}`;
}

export const CarService = {
  // Получение списка автомобилей с параметрами фильтрации
  async getCars(params: Record<string, any> = {}): Promise<ICarsResponse> {
    try {
      const { data } = await instance.get<ICarsResponse>('/cars', { params });
      return data;
    } catch (error: any) {
      console.error('Ошибка при получении списка автомобилей:', error);
      throw new Error(error.response?.data?.message || 'Не удалось получить список автомобилей');
    }
  },

  // Получение автомобилей с автосогласованием
  async getCarsWithAutoNegotiation(params: Record<string, any> = {}): Promise<ICarsResponse> {
    try {
      const requestParams = {
        ...params,
        autonegotiation: true,
      };
      const { data } = await instance.get<ICarsResponse>('/cars', { params: requestParams });
      return data;
    } catch (error: any) {
      console.error('Ошибка при получении автомобилей с автосогласованием:', error);
      throw new Error(error.response?.data?.message || 'Не удалось получить автомобили с автосогласованием');
    }
  },

  // Получение информации об отдельном автомобиле
  async getCarById(carId: string): Promise<ICar> {
    try {
      const { data } = await instance.get<ICar>(`/cars/${carId}`);
      return data;
    } catch (error: any) {
      console.error('Ошибка при получении информации об автомобиле:', error);
      throw new Error(error.response?.data?.message || 'Не удалось получить информацию об автомобиле');
    }
  },

  // Обновление информации о технике автосогласования
  async updateCarTechnik(carId: string, technikID: string | null): Promise<void> {
    try {
      await instance.put(carTechnikUrl(carId, technikID));
    } catch (error: any) {
      console.error('Ошибка при обновлении техника автосогласования:', error);
      throw toApiError(error, 'Не удалось обновить техника автосогласования');
    }
  },

  // Установка автосогласования для автомобиля
  async setAutoNegotiation(carId: string, technikID: string): Promise<void> {
    try {
      await instance.put(carTechnikUrl(carId, technikID));
    } catch (error: any) {
      console.error('Ошибка при установке автосогласования:', error);
      throw toApiError(error, 'Не удалось установить автосогласование');
    }
  },

  // Отмена автосогласования для автомобиля
  async removeAutoNegotiation(carId: string): Promise<void> {
    try {
      await instance.put(carTechnikUrl(carId, null));
    } catch (error: any) {
      console.error('Ошибка при отмене автосогласования:', error);
      throw toApiError(error, 'Не удалось отменить автосогласование');
    }
  },

  // Удаление водителя из машины
  async removeDriverFromCar(carId: string): Promise<void> {
    try {
      await instance.delete(`/cars/${carId}/driver`);
    } catch (error: any) {
      console.error('Ошибка при удалении водителя из машины:', error);
      throw new Error(error.response?.data?.message || 'Не удалось удалить водителя из машины');
    }
  },

  // Обновление срока аренды машины
  async updateCarRentExpiration(carData: { number: string; rentExpiredAt: string; driverID: string }): Promise<void> {
    try {
      await instance.put('/cars/rent/expiration', carData);
    } catch (error: any) {
      console.error('Ошибка при обновлении срока аренды:', error);
      throw new Error(error.response?.data?.message || 'Не удалось обновить срок аренды');
    }
  },

  // Получение марок автомобилей (все модели без фильтра, затем извлекаем уникальные марки)
  async getCarMarks(): Promise<string[]> {
    try {
      const { data } = await instance.get<ICarModel[]>('/car-models');
      // Извлекаем уникальные марки из всех моделей
      const uniqueMarks = [...new Set(data.map((item) => item.mark))];
      return uniqueMarks;
    } catch (error: any) {
      console.error('Ошибка при получении марок автомобилей:', error);
      throw new Error(error.response?.data?.message || 'Не удалось получить марки автомобилей');
    }
  },

  // Получение моделей автомобилей по марке
  async getCarModels(mark?: string): Promise<ICarModel[]> {
    try {
      const params = mark ? { mark } : {};
      const { data } = await instance.get<ICarModel[]>('/car-models', { params });
      return data;
    } catch (error: any) {
      console.error('Ошибка при получении моделей автомобилей:', error);
      throw new Error(error.response?.data?.message || 'Не удалось получить модели автомобилей');
    }
  },

  // Обновление данных автомобиля
  async updateCar(carId: string, carData: ICarUpdateRequest): Promise<ICar> {
    try {
      const { data } = await instance.put<ICar>(`/cars/${carId}`, carData);
      return data;
    } catch (error: any) {
      console.error('Ошибка при обновлении автомобиля:', error);
      throw new Error(error.response?.data?.message || 'Не удалось обновить данные автомобиля');
    }
  },

  // Обновление данных ОСАГО автомобиля
  async updateCarOsago(carId: string, osagoData: IOsagoUpdateRequest): Promise<ICar> {
    try {
      const { data } = await instance.put<ICar>(`/cars/${carId}/osago-details`, osagoData);
      return data;
    } catch (error: any) {
      console.error('Ошибка при обновлении данных ОСАГО:', error);
      throw new Error(error.response?.data?.message || 'Не удалось обновить данные ОСАГО');
    }
  },

  // Загрузка фото ОСАГО
  async updateCarOsagoPhoto(carId: string, photoFile: File): Promise<ICar> {
    try {
      const formData = new FormData();
      formData.append('osago', photoFile);

      const { data } = await instance.put<ICar>(`/cars/${carId}/osago-photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return data;
    } catch (error: any) {
      console.error('Ошибка при загрузке фото ОСАГО:', error);
      throw new Error(error.response?.data?.message || 'Не удалось загрузить фото ОСАГО');
    }
  },

  // Загрузка фото диагностической карты
  async updateCarDiagnosticCardPhoto(carId: string, photoFile: File): Promise<ICar> {
    try {
      const formData = new FormData();
      formData.append('photo', photoFile);

      const { data } = await instance.put<ICar>(`/cars/${carId}/diagnostic-card-photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return data;
    } catch (error: any) {
      console.error('Ошибка при загрузке фото диагностической карты:', error);
      throw new Error(error.response?.data?.message || 'Не удалось загрузить фото диагностической карты');
    }
  },

  // Обновление фотографий кузова автомобиля
  async updateCarPhotos(carId: string, photos: ICarPhotosUpdateRequest): Promise<ICar> {
    try {
      const formData = new FormData();

      // Добавляем только те фото, которые переданы
      if (photos.front) formData.append('front', photos.front);
      if (photos.back) formData.append('back', photos.back);
      if (photos.left) formData.append('left', photos.left);
      if (photos.right) formData.append('right', photos.right);
      if (photos.trunk) formData.append('trunk', photos.trunk);

      const { data } = await instance.put<ICar>(`/cars/${carId}/photos`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return data;
    } catch (error: any) {
      console.error('Ошибка при загрузке фотографий кузова:', error);
      throw new Error(error.response?.data?.message || 'Не удалось загрузить фотографии кузова');
    }
  },

  // Создание нового автомобиля
  async createCar(carData: ICreateCarRequest): Promise<ICar> {
    try {
      const { data } = await instance.post<ICar>('/cars/admin/create', carData);
      return data;
    } catch (error: any) {
      console.error('Ошибка при создании автомобиля:', error);
      throw new Error(error.response?.data?.message || 'Не удалось создать автомобиль');
    }
  },
};
